import type { Disposable } from '../core/Event';
import { DefaultRandom, type Random } from '../core/Random';
import { HigherLowerModel } from '../model/higherlower/HigherLowerModel';
import { PlayerProfile } from '../model/profile/PlayerProfile';
import { DEFAULT_THEME, type Theme } from '../theme';
import { HigherLowerCard } from '../widgets/higherlower/HigherLowerCard';
import { HigherLowerHudComponent } from './components/HigherLowerHudComponent';
import { SceneBase } from './SceneBase';
import type { SceneLayoutContext } from './SceneComponent';

const STARTING_BALANCE = 500;
const BET_PER_ROUND = 10;
/** Multiplier per streak, indexed by streak count. Streak 0 = no correct
 * guess yet, multiplier 1× (refunds bet on cash-out). Each correct guess
 * multiplies by ~1.8× (close to fair play assuming the median card draws
 * an even ~50/50 split). */
const MULTIPLIERS = [1, 1.8, 3.2, 5.8, 10, 18, 32, 60];
const CARD_GAP_PX = 32;

/**
 * Higher/Lower demo — predict whether the next card (1-13) is higher or
 * lower than the current one. Each correct guess grows the multiplier;
 * cash out to lock in {@code bet × multiplier}, or risk the next call.
 *
 * <p>The card animation is two {@link HigherLowerCard} widgets side by
 * side: the left card shows the current value face-up; the right card
 * shows face-down until the player predicts, then flips to reveal the
 * draw. After each guess the right card's value slides into the left
 * slot for the next round.
 */
export class HigherLowerDemoScene extends SceneBase {
  private currentCard: HigherLowerCard | null = null;
  private nextCard: HigherLowerCard | null = null;
  private hud: HigherLowerHudComponent | null = null;
  private readonly random: Random;
  private readonly model: HigherLowerModel;
  private modelSubscriptions: Disposable[] = [];
  private profileSubscription: Disposable | null = null;

  constructor(
    container: HTMLElement,
    theme: Theme = DEFAULT_THEME,
    profile: PlayerProfile | null = null,
  ) {
    super(container, { theme });
    this.random = new DefaultRandom();
    this.model = new HigherLowerModel(
      {
        startingBalance: profile?.balance ?? STARTING_BALANCE,
        bet: BET_PER_ROUND,
        multiplierByStreak: MULTIPLIERS,
      },
      this.random,
    );
    if (profile) {
      this.profileSubscription = this.model.events.on('balance-changed', () => {
        profile.setBalance(this.model.balance);
      });
    }
  }

  protected buildContent(): void {
    this.currentCard = new HigherLowerCard(this.model.currentCard, {
      accentColor: this.theme.palette.accent,
    });
    this.nextCard = new HigherLowerCard(1, {
      accentColor: this.theme.palette.accent,
    });
    // Both start face-down before a round starts.
    this.currentCard.showBack();
    this.nextCard.showBack();
    this.app.stage.addChild(this.currentCard, this.nextCard);

    this.hud = new HigherLowerHudComponent(this.model, {
      onPlay: () => this.handlePlay(),
      onHigher: () => void this.handlePredict('higher'),
      onLower: () => void this.handlePredict('lower'),
      onCashOut: () => this.model.cashOut(),
    });
    this.app.stage.addChild(this.hud);

    this.modelSubscriptions.push(
      this.model.events.on('round-started', (event) => {
        this.currentCard?.showFace(event.startingCard);
        this.nextCard?.showBack();
      }),
    );
  }

  protected onResize(): void {
    this.layout();
  }

  protected onKeyDown(event: KeyboardEvent): void {
    if (event.code === 'Space') {
      event.preventDefault();
      // Space = PLAY (or CASH OUT mid-streak — pick the most "step
      // forward" action depending on state).
      if (this.model.status === 'awaiting-prediction') {
        this.model.cashOut();
      } else {
        this.handlePlay();
      }
    } else if (event.code === 'ArrowUp') {
      event.preventDefault();
      if (this.model.status === 'awaiting-prediction') {
        void this.handlePredict('higher');
      }
    } else if (event.code === 'ArrowDown') {
      event.preventDefault();
      if (this.model.status === 'awaiting-prediction') {
        void this.handlePredict('lower');
      }
    }
  }

  protected disposeResources(wasInitialized: boolean): void {
    this.profileSubscription?.dispose();
    this.profileSubscription = null;
    for (const sub of this.modelSubscriptions) sub.dispose();
    this.modelSubscriptions = [];
    this.model.dispose();
    if (!wasInitialized) return;
    this.hud?.destroy();
    this.hud = null;
    this.currentCard?.destroyCard();
    this.currentCard?.destroy({ children: true });
    this.currentCard = null;
    this.nextCard?.destroyCard();
    this.nextCard?.destroy({ children: true });
    this.nextCard = null;
  }

  private layout(): void {
    if (!this.currentCard || !this.nextCard || !this.hud) return;
    const { width, height } = this.app.renderer;
    const cardWidth = 130;
    const cardHeight = Math.floor(cardWidth * 1.4);
    const totalWidth = cardWidth * 2 + CARD_GAP_PX;
    const startX = Math.round(width / 2 - totalWidth / 2);
    const cardY = Math.round(height / 2 - 64);
    this.currentCard.x = startX + cardWidth / 2;
    this.currentCard.y = cardY;
    this.nextCard.x = startX + cardWidth + CARD_GAP_PX + cardWidth / 2;
    this.nextCard.y = cardY;

    const ctx: SceneLayoutContext = {
      width,
      height,
      centerX: Math.round(width / 2),
      machineTop: cardY - cardHeight / 2,
      machineBottom: cardY + cardHeight / 2,
      spinButtonY: cardY + cardHeight / 2 + 70,
    };
    this.hud.layout(ctx);
  }

  private handlePlay(): void {
    if (this.model.status === 'awaiting-prediction') return;
    this.model.startRound();
  }

  private async handlePredict(direction: 'higher' | 'lower'): Promise<void> {
    if (!this.nextCard || !this.currentCard) return;
    const fromCard = this.model.currentCard;
    const next = this.model.predict(direction);
    if (next === null) return;
    await this.nextCard.reveal(next);

    // After a correct guess, slide the revealed card into the current
    // slot for the next prediction. After a wrong guess, leave the cards
    // in place so the player can read the result.
    if (this.model.status === 'awaiting-prediction') {
      // Correct — move next → current visually after a brief beat.
      setTimeout(() => {
        this.currentCard?.showFace(next);
        this.nextCard?.showBack();
      }, 450);
    }
    void fromCard; // referenced for clarity; unused after model handles state.
  }
}
